import { createInstance } from '@amplitude/analytics-browser';
import { BrowserClient } from '@amplitude/analytics-types';
import { ExperimentClient } from '@amplitude/experiment-js-client';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { Company } from './company';
import { refetchProfile } from './handlers/profile';
import { User } from './user';

export interface IdentityStore {
	// Configuration data
	analyticsAPIKey: string;
	analyticsServerURL?: string;
	experimentAPIKey: string;
	experimentServerURL?: string;
	isInitialized: boolean;
	onError?: (err?: unknown) => void;
	token?: string;

	// Identity information
	analyticsClient: BrowserClient;
	experimentClient: ExperimentClient;
	features: Record<string, { enabled: boolean }>;
	refetchProfile: () => Promise<{ company: Company; user: User }>;
	company: Company;
	user: User;

	// Loading states
	isLoadingCompany: boolean;
	isLoadingFeatures: boolean;
	isLoadingGlobalFeatures: boolean;
	isLoadingUser: boolean;

	areFeaturesFetched: boolean;
	isCompanyFetched: boolean;
	isUserFetched: boolean;

	// Error states
	companyError: unknown;
	featuresError: unknown;
	globalError: unknown;
	userError: unknown;
}

export const useIdentityStore = createWithEqualityFn<IdentityStore>(
	() => ({
		analyticsAPIKey: '',
		analyticsClient: createInstance(),
		analyticsServerURL: undefined,
		areFeaturesFetched: false,
		company: undefined as unknown as Company,
		companyError: undefined,
		experimentAPIKey: '',
		experimentClient: undefined as unknown as ExperimentClient,
		experimentServerURL: undefined,
		features: {},
		featuresError: undefined,
		globalError: undefined,
		isCompanyFetched: false,
		isInitialized: false,
		isLoadingCompany: false,
		isLoadingFeatures: false,
		isLoadingGlobalFeatures: false,
		isLoadingUser: false,
		isUserFetched: false,
		onError: undefined,
		refetchProfile: refetchProfile,
		token: undefined,
		user: undefined as unknown as User,
		userError: undefined,
	}),
	shallow
);
